import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { FiPlus, FiEdit2, FiX, FiSave, FiAlertCircle, FiMonitor } from 'react-icons/fi';
import { colors } from '../../theme/colors';
import { collection, addDoc, updateDoc, doc, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../context/AuthContext';
import PageLayout from '../../components/PageLayout';
import usePagePermissions from '../../hooks/usePagePermissions';
import useEscapeClose from '../../hooks/useEscapeClose';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
  gap: 16px;
  flex-wrap: wrap;
`;

const HeaderText = styled.div``;

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: ${colors.text.primary};
  margin: 0 0 8px 0;
`;

const PageDescription = styled.p`
  font-size: 14px;
  color: ${colors.text.secondary};
  margin: 0;
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: ${colors.primary.main};
  color: white;
  border: none;
  border-radius: ${colors.borderRadius.md};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${colors.primary.dark};
    transform: translateY(-1px);
  }
`;

const ContentCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  overflow: hidden;
`;

const TableContainer = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Thead = styled.thead`
  background: ${colors.neutral[50]};
`;

const Th = styled.th`
  padding: 14px 16px;
  text-align: left;
  font-size: 12px;
  font-weight: 600;
  color: ${colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;
`;

const Tbody = styled.tbody``;

const Tr = styled.tr`
  border-bottom: 1px solid ${colors.neutral[200]};
  transition: background 0.2s;

  &:hover {
    background: ${colors.neutral[50]};
  }

  &:last-child {
    border-bottom: none;
  }
`;

const Td = styled.td`
  padding: 16px;
  font-size: 14px;
  color: ${colors.text.primary};
  vertical-align: middle;
`;

const CounterCode = styled.span`
  font-family: 'SF Mono', 'Consolas', monospace;
  font-weight: 600;
  color: ${colors.primary.main};
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border-radius: ${colors.borderRadius.sm};
  font-size: 12px;
  font-weight: 500;
  background: ${props => props.$active ? colors.success.main + '20' : colors.neutral[200]};
  color: ${props => props.$active ? colors.success.main : colors.text.secondary};
`;

const DefaultBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: ${colors.borderRadius.sm};
  font-size: 10px;
  font-weight: 600;
  background: ${colors.warning.main}20;
  color: ${colors.warning.main};
  text-transform: uppercase;
  margin-left: 8px;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
`;

const IconButton = styled.button`
  padding: 8px;
  background: ${props => props.$danger ? colors.error.main + '15' : colors.neutral[100]};
  color: ${props => props.$danger ? colors.error.main : colors.text.secondary};
  border: none;
  border-radius: ${colors.borderRadius.sm};
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: ${props => props.$danger ? colors.error.main + '25' : colors.neutral[200]};
    color: ${props => props.$danger ? colors.error.main : colors.text.primary};
  }
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: ${colors.borderRadius.sm};
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid;

  ${props => props.$primary && `
    background: ${colors.primary.main};
    border-color: ${colors.primary.main};
    color: white;

    &:hover {
      background: ${colors.primary.dark};
      border-color: ${colors.primary.dark};
    }
  `}

  ${props => props.$warning && `
    background: ${colors.warning.main}20;
    border-color: ${colors.warning.main};
    color: ${colors.warning.main};

    &:hover {
      background: ${colors.warning.main}30;
    }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CellInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const CellName = styled.span`
  font-weight: 500;
  color: ${colors.text.primary};
`;

const CellDetail = styled.span`
  font-size: 12px;
  color: ${colors.text.secondary};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: ${colors.text.secondary};
`;

const EmptyIcon = styled.div`
  font-size: 48px;
  margin-bottom: 16px;
  color: ${colors.neutral[300]};
`;

const EmptyText = styled.p`
  font-size: 16px;
  margin: 0 0 8px 0;
`;

const EmptySubtext = styled.p`
  font-size: 14px;
  margin: 0;
`;

const Alert = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-radius: ${colors.borderRadius.md};
  font-size: 14px;
  margin-bottom: 20px;
  background: ${props => props.$type === 'success' ? colors.success.main + '15' : colors.error.main + '15'};
  color: ${props => props.$type === 'success' ? colors.success.main : colors.error.main};
  border: 1px solid ${props => props.$type === 'success' ? colors.success.main + '30' : colors.error.main + '30'};
`;

// Modal Styles
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  width: 100%;
  max-width: 540px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: ${colors.shadows.lg};
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid ${colors.neutral[200]};
`;

const ModalTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: ${colors.text.primary};
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 20px;
  color: ${colors.text.secondary};
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${colors.borderRadius.sm};
  transition: all 0.2s;

  &:hover {
    background: ${colors.neutral[100]};
    color: ${colors.text.primary};
  }
`;

const ModalBody = styled.div`
  padding: 24px;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid ${colors.neutral[200]};
  background: ${colors.neutral[50]};
`;

const FormGroup = styled.div`
  margin-bottom: 20px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const Label = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: ${colors.text.primary};
  margin-bottom: 6px;

  span {
    color: ${colors.error.main};
    margin-left: 2px;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 14px;
  color: ${colors.text.primary};
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 3px ${colors.primary.main}15;
  }

  &:disabled {
    background: ${colors.neutral[100]};
    cursor: not-allowed;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 14px;
  color: ${colors.text.primary};
  background: white;
  cursor: pointer;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 3px ${colors.primary.main}15;
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
`;

const Hint = styled.p`
  font-size: 12px;
  color: ${colors.text.secondary};
  margin: 6px 0 0 0;
`;

const UserList = styled.div`
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  max-height: 160px;
  overflow-y: auto;
  padding: 4px;
`;

const UserRow = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border-radius: ${colors.borderRadius.sm};
  cursor: pointer;
  font-size: 14px;
  color: ${colors.text.primary};

  &:hover {
    background: ${colors.neutral[50]};
  }

  input {
    width: 16px;
    height: 16px;
    cursor: pointer;
  }
`;

const CancelButton = styled.button`
  padding: 10px 20px;
  background: white;
  color: ${colors.text.primary};
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${colors.neutral[50]};
  }
`;

const SaveButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: ${colors.primary.main};
  color: white;
  border: none;
  border-radius: ${colors.borderRadius.md};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: ${colors.primary.dark};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const emptyForm = {
  counterCode: '',
  counterName: '',
  branchId: '',
  cashAccountId: '',
  assignedUserIds: [],
  isDefault: false,
  isActive: true
};

const CounterSetup = () => {
  const { activeCompany, currentUser, companyUser, isBranchModuleEnabled } = useAuth();
  const { canEdit } = usePagePermissions('settings', 'posCounterSetup');
  const branchEnabled = typeof isBranchModuleEnabled === 'function' ? isBranchModuleEnabled() : false;

  const [counters, setCounters] = useState([]);
  const [branches, setBranches] = useState([]);
  const [users, setUsers] = useState([]);
  const [cashAccounts, setCashAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  useEscapeClose(showModal, () => setShowModal(false));
  const [editingCounter, setEditingCounter] = useState(null);
  const [alert, setAlert] = useState({ show: false, type: '', message: '' });
  const [formData, setFormData] = useState(emptyForm);

  const isAdmin = companyUser?.role === 'admin';

  const loadData = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      setLoading(true);
      const cid = activeCompany.id;
      const [countersSnap, branchesSnap, usersSnap, accountsSnap] = await Promise.all([
        getDocs(query(collection(db, 'companies', cid, 'posCounters'), orderBy('counterName'))),
        getDocs(collection(db, 'companies', cid, 'branches')),
        getDocs(collection(db, 'companies', cid, 'users')),
        getDocs(collection(db, 'companies', cid, 'accounts')),
      ]);

      setCounters(countersSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setBranches(branchesSnap.docs.map(d => ({ id: d.id, ...d.data() })).filter(b => b.isActive !== false));
      // Only show assignable operators. Admins and the company owner already see
      // every counter (seeAllCounters), so they are not listed as cashiers.
      const ownerId = activeCompany?.ownerId || null;
      setUsers(
        usersSnap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(u => u.isActive !== false && u.role !== 'admin' && u.id !== ownerId)
      );
      // Cash accounts only (Cash in Hand group 11101), postable level-5 leaves.
      setCashAccounts(
        accountsSnap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(a => String(a.code || '').startsWith('11101') && a.level === 5)
          .sort((a, b) => String(a.code).localeCompare(String(b.code)))
      );
    } catch (error) {
      console.error('Error loading counters:', error);
      setAlert({ show: true, type: 'error', message: 'Failed to load POS counters' });
    } finally {
      setLoading(false);
    }
  }, [activeCompany]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const toggleUser = (userId) => {
    setFormData(prev => ({
      ...prev,
      assignedUserIds: prev.assignedUserIds.includes(userId)
        ? prev.assignedUserIds.filter(id => id !== userId)
        : [...prev.assignedUserIds, userId]
    }));
  };

  const openAddModal = () => {
    if (!canEdit) { alert('You do not have permission to save changes.'); return; }
    setEditingCounter(null);
    setFormData({ ...emptyForm, isDefault: counters.length === 0 });
    setShowModal(true);
  };

  const openEditModal = (counter) => {
    if (!canEdit) { alert('You do not have permission to save changes.'); return; }
    setEditingCounter(counter);
    setFormData({
      counterCode: counter.counterCode || '',
      counterName: counter.counterName || '',
      branchId: counter.branchId || '',
      cashAccountId: counter.cashAccountId || '',
      assignedUserIds: Array.isArray(counter.assignedUserIds) ? counter.assignedUserIds : [],
      isDefault: counter.isDefault || false,
      isActive: counter.isActive !== false
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canEdit) { alert('You do not have permission to save changes.'); return; }

    if (!formData.counterCode || !formData.counterName) {
      setAlert({ show: true, type: 'error', message: 'Counter code and name are required' });
      return;
    }
    if (!formData.cashAccountId) {
      setAlert({ show: true, type: 'error', message: 'Select a cash account for this counter (create one in Chart of Accounts under Cash in Hand if none exist).' });
      return;
    }

    // Duplicate counter code guard
    const dup = counters.find(
      c => String(c.counterCode).toLowerCase() === formData.counterCode.toLowerCase() && c.id !== editingCounter?.id
    );
    if (dup) {
      setAlert({ show: true, type: 'error', message: 'A counter with this code already exists' });
      return;
    }

    // Package limit guard (creation only). Editing existing counters is always allowed.
    const maxPosCounters = Number(activeCompany?.maxPosCounters) || 0;
    if (!editingCounter && maxPosCounters > 0 && counters.length >= maxPosCounters) {
      setAlert({
        show: true,
        type: 'error',
        message: `Your package allows up to ${maxPosCounters} POS counter${maxPosCounters === 1 ? '' : 's'}. Contact your administrator to upgrade.`
      });
      return;
    }

    setSaving(true);
    try {
      const cid = activeCompany.id;
      const countersRef = collection(db, 'companies', cid, 'posCounters');
      const branch = branches.find(b => b.id === formData.branchId);
      const cashAcc = cashAccounts.find(a => a.id === formData.cashAccountId);

      // Only one default counter at a time — clear default from others.
      if (formData.isDefault) {
        const currentDefault = counters.find(c => c.isDefault && c.id !== editingCounter?.id);
        if (currentDefault) {
          await updateDoc(doc(db, 'companies', cid, 'posCounters', currentDefault.id), {
            isDefault: false, updatedAt: new Date().toISOString(), updatedBy: currentUser?.uid
          });
        }
      }

      const payload = {
        counterCode: formData.counterCode.trim(),
        counterName: formData.counterName.trim(),
        branchId: branchEnabled ? (formData.branchId || null) : null,
        branchName: branchEnabled ? (branch?.branchName || null) : null,
        cashAccountId: formData.cashAccountId,
        cashAccountCode: cashAcc?.code || null,
        cashAccountName: cashAcc?.name || null,
        assignedUserIds: formData.assignedUserIds,
        isDefault: formData.isDefault,
        isActive: formData.isActive,
        updatedAt: new Date().toISOString(),
        updatedBy: currentUser?.uid
      };

      if (editingCounter) {
        await updateDoc(doc(db, 'companies', cid, 'posCounters', editingCounter.id), payload);
        setAlert({ show: true, type: 'success', message: 'Counter updated successfully' });
      } else {
        await addDoc(countersRef, {
          ...payload,
          createdAt: new Date().toISOString(),
          createdBy: currentUser?.uid
        });
        setAlert({ show: true, type: 'success', message: 'Counter created successfully' });
      }

      setShowModal(false);
      loadData();
    } catch (error) {
      console.error('Error saving counter:', error);
      setAlert({ show: true, type: 'error', message: 'Failed to save counter' });
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (counter) => {
    if (!canEdit) { alert('You do not have permission to save changes.'); return; }
    if (!isAdmin) return;
    if (counter.isDefault && counter.isActive) {
      setAlert({ show: true, type: 'error', message: 'Cannot deactivate the default counter. Set another counter as default first.' });
      return;
    }
    try {
      await updateDoc(doc(db, 'companies', activeCompany.id, 'posCounters', counter.id), {
        isActive: !counter.isActive, updatedAt: new Date().toISOString(), updatedBy: currentUser?.uid
      });
      setAlert({ show: true, type: 'success', message: `Counter ${counter.isActive ? 'deactivated' : 'activated'} successfully` });
      loadData();
    } catch (error) {
      console.error('Error toggling counter status:', error);
      setAlert({ show: true, type: 'error', message: 'Failed to update counter status' });
    }
  };

  const handleSetDefault = async (counter) => {
    if (!canEdit) { alert('You do not have permission to save changes.'); return; }
    if (!isAdmin) return;
    if (!counter.isActive) {
      setAlert({ show: true, type: 'error', message: 'Cannot set an inactive counter as default' });
      return;
    }
    try {
      const currentDefault = counters.find(c => c.isDefault);
      if (currentDefault) {
        await updateDoc(doc(db, 'companies', activeCompany.id, 'posCounters', currentDefault.id), {
          isDefault: false, updatedAt: new Date().toISOString(), updatedBy: currentUser?.uid
        });
      }
      await updateDoc(doc(db, 'companies', activeCompany.id, 'posCounters', counter.id), {
        isDefault: true, updatedAt: new Date().toISOString(), updatedBy: currentUser?.uid
      });
      setAlert({ show: true, type: 'success', message: `"${counter.counterName}" is now the default counter` });
      loadData();
    } catch (error) {
      console.error('Error setting default counter:', error);
      setAlert({ show: true, type: 'error', message: 'Failed to set default counter' });
    }
  };

  const userNamesFor = (ids) => {
    if (!Array.isArray(ids) || ids.length === 0) return '-';
    const names = ids.map(id => {
      const u = users.find(x => x.id === id);
      return u ? (u.name || u.displayName || u.email || id) : id;
    });
    return names.join(', ');
  };

  return (
    <PageLayout pageTitle="POS Counters" breadcrumb="Settings > POS Counters">
      <Container>
        <PageHeader>
          <HeaderText>
            <PageTitle>POS Counters</PageTitle>
            <PageDescription>
              Each counter has its own cash account, assigned users, and shift/closing. A counter is required to use the POS.
            </PageDescription>
          </HeaderText>
          {isAdmin && canEdit && (
            <AddButton onClick={openAddModal}>
              <FiPlus />
              Add POS Counter
            </AddButton>
          )}
        </PageHeader>

        {alert.show && (
          <Alert $type={alert.type}>
            <FiAlertCircle />
            {alert.message}
          </Alert>
        )}

        <ContentCard>
          {loading ? (
            <EmptyState><EmptyText>Loading counters...</EmptyText></EmptyState>
          ) : counters.length === 0 ? (
            <EmptyState>
              <EmptyIcon><FiMonitor /></EmptyIcon>
              <EmptyText>No POS Counters Created</EmptyText>
              <EmptySubtext>Create your first counter to start using the POS.</EmptySubtext>
            </EmptyState>
          ) : (
            <TableContainer>
              <Table>
                <Thead>
                  <tr>
                    <Th>Code</Th>
                    <Th>Counter</Th>
                    {branchEnabled && <Th>Branch</Th>}
                    <Th>Cash Account</Th>
                    <Th>Assigned Users</Th>
                    <Th>Status</Th>
                    {isAdmin && canEdit && <Th>Actions</Th>}
                  </tr>
                </Thead>
                <Tbody>
                  {counters.map(counter => (
                    <Tr key={counter.id}>
                      <Td><CounterCode>{counter.counterCode}</CounterCode></Td>
                      <Td>
                        <CellInfo>
                          <CellName>
                            {counter.counterName}
                            {counter.isDefault && <DefaultBadge>Default</DefaultBadge>}
                          </CellName>
                        </CellInfo>
                      </Td>
                      {branchEnabled && <Td>{counter.branchName || '-'}</Td>}
                      <Td>
                        <CellInfo>
                          <CellDetail>{counter.cashAccountCode || '-'}</CellDetail>
                          <CellDetail>{counter.cashAccountName || ''}</CellDetail>
                        </CellInfo>
                      </Td>
                      <Td><CellDetail>{userNamesFor(counter.assignedUserIds)}</CellDetail></Td>
                      <Td>
                        <Badge $active={counter.isActive}>
                          {counter.isActive ? 'Active' : 'Inactive'}
                        </Badge>
                      </Td>
                      {isAdmin && canEdit && (
                        <Td>
                          <ActionButtons>
                            <IconButton onClick={() => openEditModal(counter)} title="Edit counter">
                              <FiEdit2 size={14} />
                            </IconButton>
                            {!counter.isDefault && counter.isActive && (
                              <ActionButton $primary onClick={() => handleSetDefault(counter)} title="Set as default counter">
                                Set Default
                              </ActionButton>
                            )}
                            <ActionButton
                              $warning={counter.isActive}
                              $primary={!counter.isActive}
                              onClick={() => handleToggleActive(counter)}
                              title={counter.isActive ? 'Deactivate counter' : 'Activate counter'}
                            >
                              {counter.isActive ? 'Deactivate' : 'Activate'}
                            </ActionButton>
                          </ActionButtons>
                        </Td>
                      )}
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          )}
        </ContentCard>

        {showModal && (
          <ModalOverlay>
            <ModalContent onClick={e => e.stopPropagation()}>
              <ModalHeader>
                <ModalTitle>{editingCounter ? 'Edit POS Counter' : 'Add POS Counter'}</ModalTitle>
                <CloseButton onClick={() => setShowModal(false)}><FiX /></CloseButton>
              </ModalHeader>
              <form onSubmit={handleSubmit}>
                <ModalBody>
                  <FormRow>
                    <FormGroup>
                      <Label>Counter Code <span>*</span></Label>
                      <Input
                        type="text"
                        name="counterCode"
                        value={formData.counterCode}
                        onChange={handleChange}
                        placeholder="e.g., T1"
                        required
                      />
                      <Hint>Used in document numbers, e.g. PS-{(formData.counterCode || 'T1')}-00001</Hint>
                    </FormGroup>
                    <FormGroup>
                      <Label>Counter Name <span>*</span></Label>
                      <Input
                        type="text"
                        name="counterName"
                        value={formData.counterName}
                        onChange={handleChange}
                        placeholder="e.g., Front Till"
                        required
                      />
                    </FormGroup>
                  </FormRow>

                  {branchEnabled && (
                    <FormGroup>
                      <Label>Branch</Label>
                      <Select name="branchId" value={formData.branchId} onChange={handleChange}>
                        <option value="">— No branch —</option>
                        {branches.map(b => (
                          <option key={b.id} value={b.id}>{b.branchName} ({b.branchCode})</option>
                        ))}
                      </Select>
                    </FormGroup>
                  )}

                  <FormGroup>
                    <Label>Cash Account <span>*</span></Label>
                    <Select name="cashAccountId" value={formData.cashAccountId} onChange={handleChange} required>
                      <option value="">— Select cash account —</option>
                      {cashAccounts.map(a => (
                        <option key={a.id} value={a.id}>{a.code} — {a.name}</option>
                      ))}
                    </Select>
                    <Hint>
                      {cashAccounts.length === 0
                        ? 'No cash accounts found. Create one in Chart of Accounts under "Cash in Hand" (11101) first.'
                        : 'Each counter should use its OWN cash account so its drawer reconciles independently.'}
                    </Hint>
                  </FormGroup>

                  <FormGroup>
                    <Label>Assigned Users</Label>
                    {users.length === 0 ? (
                      <Hint>No users found.</Hint>
                    ) : (
                      <UserList>
                        {users.map(u => (
                          <UserRow key={u.id}>
                            <input
                              type="checkbox"
                              checked={formData.assignedUserIds.includes(u.id)}
                              onChange={() => toggleUser(u.id)}
                            />
                            {u.name || u.displayName || u.email || u.id}
                          </UserRow>
                        ))}
                      </UserList>
                    )}
                    <Hint>These users will see and operate this counter in the POS.</Hint>
                  </FormGroup>

                  <FormRow>
                    <FormGroup>
                      <Label>Status</Label>
                      <Select
                        name="isActive"
                        value={formData.isActive ? 'true' : 'false'}
                        onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.value === 'true' }))}
                      >
                        <option value="true">Active</option>
                        <option value="false">Inactive</option>
                      </Select>
                    </FormGroup>
                    <FormGroup>
                      <Label>Default Counter</Label>
                      <Select
                        name="isDefault"
                        value={formData.isDefault ? 'true' : 'false'}
                        onChange={(e) => setFormData(prev => ({ ...prev, isDefault: e.target.value === 'true' }))}
                      >
                        <option value="false">No</option>
                        <option value="true">Yes</option>
                      </Select>
                    </FormGroup>
                  </FormRow>
                </ModalBody>
                <ModalFooter>
                  <CancelButton type="button" onClick={() => setShowModal(false)}>Cancel</CancelButton>
                  {canEdit && (
                    <SaveButton type="submit" disabled={saving}>
                      <FiSave />
                      {saving ? 'Saving...' : editingCounter ? 'Update Counter' : 'Create Counter'}
                    </SaveButton>
                  )}
                </ModalFooter>
              </form>
            </ModalContent>
          </ModalOverlay>
        )}
      </Container>
    </PageLayout>
  );
};

export default CounterSetup;
